
<script>
import { mapGetters } from 'vuex';
import tailwindConfig from '@/tailwind.config.js';
import ProductCustomisationCanvas from '@/components/ProductCustomisationCanvas.vue';
import Carousel from '@/components/Carousel.vue';

import 'vue-slick-carousel/dist/vue-slick-carousel.css';
import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css';

export default {
    components: {
        ProductCustomisationCanvas,
        Carousel,
    },

    props: {
        images: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            activeImageIndex: 0,
            hideWrapperFadeStart: false,
            hideWrapperFadeEnd: false,
            hideWrapperFadeStartMobile: false,
            hideWrapperFadeEndMobile: false,
        };
    },

    computed: {
        ...mapGetters('product', [
            'showProductCustomisationImage',
            'activeVariationImage',
        ]),

        isMobile: {
            get() {
                return typeof window !== 'undefined' && window.innerWidth < parseInt(tailwindConfig.theme.screens.md, 10);
            },
            cache: false,
        },
    },

    mounted() {
        if (this.isMobile) {
            this.showHideThumbnailScrollFadeMobile();
        } else {
            this.showHideThumbnailScrollFade();
        }
    },

    methods: {
        showHideThumbnailScrollFadeMobile() {
            const { wrapperMobile } = this.$refs;
            if (wrapperMobile) {
                this.hideWrapperFadeStartMobile = wrapperMobile.scrollLeft === 0;
                this.hideWrapperFadeEndMobile = wrapperMobile.clientWidth + wrapperMobile.scrollLeft === wrapperMobile.scrollWidth;
            }
        },
        showHideThumbnailScrollFade() {
            const { wrapper } = this.$refs;
            if (wrapper) {
                this.hideWrapperFadeStart = wrapper.scrollTop === 0;
                this.hideWrapperFadeEnd = wrapper.clientHeight + wrapper.scrollTop === wrapper.scrollHeight;
            }
        },
        isImageActive(index) {
            return this.activeImageIndex === index;
        },
        setActiveImageIndex(index) {
            this.$store.dispatch('product/setShowProductCustomisationImage', false);
            this.$store.dispatch('product/setActiveVariationImage', null);
            this.activeImageIndex = index;
        },
        updateCanvasPreview(image) {
            this.$store.dispatch('product/setCanvasPreview', image);
        },
    },

};
</script>

<template>
    <client-only>
        <div class="block lg:flex">

            <div
                v-if="images.length > 1"
                class="relative mr-3 hidden md:block w-1/6 flex-shrink-0 flex-grow-0 basis-1/6 thumbnails-container"
            >
                <div
                    ref="wrapper"
                    class="thumbnails flex flex-col gap-4 overflow-y-scroll"
                    @scroll="showHideThumbnailScrollFade"
                >
                    <button
                        v-for="(image, index) in images"
                        :key="index"
                    >
                        <img

                            v-lazy="image.sizes.small"
                            :alt="image.alt"
                            :class="{ 'opacity-50': !isImageActive(index) }"
                            class="thumbnails__image mb-3"
                            @click="setActiveImageIndex(index)"
                        >
                    </button>

                </div>
            </div>

            <div class="md:w-5/6 flex-shrink-0 flex-grow-0 basis-5/6">
                <ProductCustomisationCanvas
                    v-if="showProductCustomisationImage && !activeVariationImage"
                    @change="updateCanvasPreview($event)"
                />
                <template v-else-if="activeVariationImage">
                    <img
                        :src="activeVariationImage"
                        class="image"
                    >
                </template>
                <Carousel
                    v-else-if="images.length > 0"
                    ref="carousel"
                    :arrows="true"
                    :dots="true"
                    :active-slide="activeImageIndex"
                    class="w-full"
                    :slides-count="images.length"
                >
                    <div
                        v-for="(image, index) in images"
                        :key="index"
                    >
                        <img
                            :src="image.sizes.large"
                            class="image"
                            alt="Product image"
                        >
                    </div>

                </Carousel>
            </div>
        </div>
    </client-only>
</template>

<style lang="scss" scoped>

.thumbnails {
    @apply relative;
    max-height: 600px;
    max-width: 600px;
    &__image {
        @apply block rounded cursor-pointer transition-fast;
        object-fit: cover;
        object-position: center;
        &:last-child {
            @apply mb-0;
        }
        &:hover {
            @apply opacity-100;
        }
    }
    &__scrollfade {
        @apply absolute pointer-events-none transition-slow z-10;
        &--top {
            top: 0;
            background: linear-gradient(
                180deg,
                rgba(255, 255, 255, 1) 0%,
                rgba(255, 255, 255, 1) 25%,
                rgba(0, 0, 0, 0) 100%
            );
            @apply w-full h-16;
        }
        &--bottom {
            bottom: 0;
            background: linear-gradient(
                0deg,
                rgba(255, 255, 255, 1) 0%,
                rgba(255, 255, 255, 1) 25%,
                rgba(0, 0, 0, 0) 100%
            );
            @apply w-full h-16;
        }
        &--left {
            bottom: 0;
            left: 0;
            background: linear-gradient(
                90deg,
                rgba(255, 255, 255, 1) 0%,
                rgba(255, 255, 255, 1) 25%,
                rgba(0, 0, 0, 0) 100%
            );
            @apply h-full w-16;
        }
        &--right {
            bottom: 0;
            right: 0;
            background: linear-gradient(
                -90deg,
                rgba(255, 255, 255, 1) 0%,
                rgba(255, 255, 255, 1) 25%,
                rgba(0, 0, 0, 0) 100%
            );
            @apply h-full w-16;
        }
    }
}
.image {
    @apply aspect-square;
}
</style>
